import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { useAtom } from "jotai";
import { Suspense, useEffect, useState } from "react";
import { useFragment, useQueryLoader } from "react-relay";

import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Loader } from "@whoosh/design-system/src/components/display";
import { ArrowLeftIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  VStack,
  Text,
  Box,
  Stack,
} from "@whoosh/design-system/src/components/primitive";

import { ClubStackNavigatorParamList } from "../../../../../navigators/ClubNavigator";
import { LessonsStackNavigatorParamList } from "../../../../../navigators/LessonsStackNavigator/types";
import { durationAtom, lessonTypeIdAtom } from "../atoms";
import { InstructorBio, InstructorBioQueryString } from "./InstructorBio";
import { InstructorLessonTypeSelect } from "./InstructorLessonTypeSelect";
import { InstructorPhoto } from "./InstructorPhoto";
import { InstructorBioQuery } from "./__generated__/InstructorBioQuery.graphql";
import { InstructorHeaderFragment_session$key } from "./__generated__/InstructorHeaderFragment_session.graphql";
import { InstructorHeaderFragment_staffProfile$key } from "./__generated__/InstructorHeaderFragment_staffProfile.graphql";

type Props = {
  instructorRef: InstructorHeaderFragment_staffProfile$key | null;
  sessionRef: InstructorHeaderFragment_session$key | null;
  scrolled: boolean;
};

export const InstructorHeader = ({
  instructorRef,
  sessionRef,
  scrolled,
}: Props) => {
  const [duration, setDuration] = useAtom(durationAtom);
  const [lessonTypeId, setLessonTypeId] = useAtom(lessonTypeIdAtom);

  const route =
    useRoute<RouteProp<LessonsStackNavigatorParamList, "InstructorDetail">>();

  const facilitySlug = route?.params?.facilitySlug;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();

  const lessonNavigation =
    useNavigation<NavigationProp<LessonsStackNavigatorParamList>>();

  const isMobile = useResponsiveProp({ base: 1, lg: 0 });

  const instructor = useFragment(InstructorHeaderFragmentString, instructorRef);

  const instructorId = instructor?.id;
  const instructorName = instructor?.user?.fullName || "";
  const instructorHeadline =
    instructor?.instructorDepartmentProfile.headline || "";

  const [isBioModalOpen, setIsBioModalOpen] = useState(false);
  const session = useFragment(
    InstructorHeaderSessionFragmentString,
    sessionRef
  );

  const membershipTypeId =
    session?.currentClientProfile?.member?.membershipType?.id;

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      lessonNavigation.navigate("LessonsHome", { facilitySlug });
    }
  };

  const onChange = (lessonTypeId: string, duration: number) => {
    setDuration(duration);
    setLessonTypeId(lessonTypeId);
  };

  const [instructorBioQueryRef, loadInstructorBioQuery] =
    useQueryLoader<InstructorBioQuery>(InstructorBioQueryString);

  useEffect(() => {
    if (instructorId && membershipTypeId && facilitySlug) {
      loadInstructorBioQuery(
        { instructorId, facilitySlug, membershipTypeId },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [facilitySlug, instructorId, loadInstructorBioQuery, membershipTypeId]);

  return instructorId ? (
    <ScreenHeader
      isRounded={true}
      scrolled={scrolled}
      variant={isMobile ? "CENTERED" : "REGULAR"}
      leftItem={
        <Button
          variant="secondary"
          leftIcon={<ArrowLeftIcon />}
          onPress={handleBackPress}
          testID="mobile-header-back-button"
          accessibilityLabel={t`Back button`}
        />
      }
      bottomComponent={
        <VStack gap={6}>
          <Stack
            gap={6}
            flexDirection={isMobile ? "row-reverse" : "column"}
            justifyContent="space-between"
          >
            <InstructorPhoto
              instructorRef={instructor}
              size={isMobile ? "mdlg" : "2xl"}
            />
            <VStack flex={1} minWidth={0}>
              <Text
                variant={isMobile ? "mainHeading" : "mainTitle2"}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {instructorName}
              </Text>
              <Text
                variant={"mainSmallMedium"}
                color="text-secondary"
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {instructorHeadline}
              </Text>
              <Box pt={3} width={63}>
                <Button
                  variant="secondary"
                  size="tiny"
                  onPress={() => setIsBioModalOpen(true)}
                  accessibilityLabel={t`Read BIO button`}
                >
                  {t`Read BIO`}
                </Button>
              </Box>
            </VStack>
          </Stack>
          <InstructorLessonTypeSelect
            facilitySlug={facilitySlug}
            sessionRef={session ?? null}
            onChange={onChange}
            instructorId={instructorId}
            duration={duration}
            lessonTypeId={lessonTypeId}
            testID={`instructor-lesson-type-select-${instructorId}`}
          />
          <Suspense fallback={<Loader />}>
            {instructorBioQueryRef ? (
              <StandardModal
                isOpen={isBioModalOpen}
                onClose={() => setIsBioModalOpen(false)}
                content={<InstructorBio queryRef={instructorBioQueryRef} />}
              />
            ) : null}
          </Suspense>
        </VStack>
      }
    />
  ) : null;
};

const InstructorHeaderFragmentString = graphql`
  fragment InstructorHeaderFragment_staffProfile on StaffProfile {
    id
    ...InstructorPhotoFragment_staffProfile
    user {
      fullName
    }
    instructorDepartmentProfile(facilitySlug: $facilitySlug) {
      headline
    }
  }
`;

const InstructorHeaderSessionFragmentString = graphql`
  fragment InstructorHeaderFragment_session on Session {
    ...InstructorLessonTypeSelect_session
    currentClientProfile {
      member {
        membershipType {
          id
        }
        ... on Customer {
          id
        }
        ... on Member {
          id
        }
      }
    }
  }
`;
